import {
  LayoutDashboard,
  Contact2,
  Paperclip,
  Briefcase,
  List,
  Calendar,
  Landmark,
  Receipt,
  DollarSign,
  Coins,
  ChartLine,
  Users,
  UserX2,
  MessageCircle,
  BarChart,
  Settings,
  type LucideIcon,
} from 'lucide-react';

import type { RoleType } from '@/features/auth/types';
import { ROUTES } from './routes';

export interface NavSubItem {
  titleKey: string;
  url: string;
  resource?: string;
  action?: string;
  roleType?: RoleType; // Show only for this role type
  excludeRoleType?: RoleType; // Hide for this role type
}

export interface NavItem {
  titleKey: string; // i18n key for title
  url: string;
  icon: LucideIcon;
  resource?: string; // permission resource
  action?: string; // permission action
  permissions?: Array<{ resource: string; action: string }>; // visible if user has ANY of these
  staffOnly?: boolean; // Hide from staff users
  roleType?: RoleType; // Show only for this role type
  items?: NavSubItem[]; // Sub-items for collapsible menu
}

// Navigation items for the sidebar
export const NAV_ITEMS: NavItem[] = [
  {
    titleKey: 'common:navigation.dashboard',
    url: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    permissions: [
      { resource: 'md-statistic', action: 'get' },
      { resource: 'partner-statistic', action: 'get' },
      { resource: 'accountant-statistic', action: 'get' },
      { resource: 'lawyer-statistic', action: 'get' },
    ],
  },
  {
    titleKey: 'common:navigation.clients',
    url: ROUTES.CLIENTS,
    icon: Contact2,
    resource: 'client',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.contracts',
    url: ROUTES.CONTRACTS,
    icon: Paperclip,
    resource: 'contract',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.matters',
    url: ROUTES.MATTERS,
    icon: Briefcase,
    resource: 'matter',
    action: 'get',
  },
  // {
  //   titleKey: 'common:navigation.tasks',
  //   url: '/app/tasks',
  //   icon: List,
  //   resource: 'task',
  //   action: 'get',
  // },
  {
    titleKey: 'common:navigation.newTasks',
    url: '/app/new-tasks',
    icon: List,
    resource: 'task',
    action: 'get',
    items: [
      {
        titleKey: 'common:navigation.myTasks',
        url: '/app/my-tasks',
        resource: 'task',
        action: 'get',
        // Manager-type users are never task assignees (they can't be matter
        // team members), so their My Tasks board can never have content.
        excludeRoleType: 'MANAGER',
      },
      {
        // All three review boards share the "Review" label — each is role-gated,
        // so only one ever shows per user.
        titleKey: 'common:navigation.review',
        url: '/app/lead-review',
        resource: 'task',
        action: 'get',
        roleType: 'LEAD',
      },
      {
        titleKey: 'common:navigation.review',
        url: '/app/partner-review',
        resource: 'task',
        action: 'get',
        roleType: 'PARTNER',
      },
      {
        titleKey: 'common:navigation.review',
        url: '/app/approve-to-billing',
        resource: 'task',
        action: 'accept',
        roleType: 'MD',
      },
    ],
  },
  {
    titleKey: 'common:navigation.calendar',
    url: ROUTES.CALENDAR,
    icon: Calendar,
    resource: 'event',
    action: 'get',
  },
  {
    // Case 360° (Stage 8) — LEO Reference directory
    titleKey: 'common:navigation.leo',
    url: '/app/leo',
    icon: Landmark,
    resource: 'leo',
    action: 'get',
  },
  {
    titleKey: 'common:navigation.bills',
    url: '/app/bills',
    icon: Receipt,
    resource: 'bill',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.payments',
    url: '/app/payments',
    icon: DollarSign,
    resource: 'payment',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.expenses',
    url: '/app/expenses',
    icon: Coins,
    resource: 'expense',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.kpi',
    url: '/app/kpi',
    icon: ChartLine,
    resource: 'kpi',
    action: 'get',
  },
  {
    titleKey: 'common:navigation.users',
    url: '/app/users',
    icon: Users,
    resource: 'user',
    action: 'get',
    staffOnly: true,
  },
  {
    titleKey: 'common:navigation.reviews',
    url: '/app/reviews',
    icon: UserX2,
    resource: 'review',
    action: 'get',
  },
  {
    titleKey: 'common:navigation.complaints',
    url: '/app/complaints',
    icon: MessageCircle,
    resource: 'complaint',
    action: 'get',
  },
  {
    titleKey: 'common:navigation.reports',
    url: ROUTES.REPORTS,
    icon: BarChart,
    resource: 'contract-report',
    action: 'get',
  },
  {
    titleKey: 'common:navigation.settings',
    url: '/app/settings',
    icon: Settings,
    staffOnly: true,
    items: [
      {
        titleKey: 'common:navigation.activityTypes',
        url: '/app/settings/activity-types',
        resource: 'activity-type',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.taskTypes',
        url: '/app/settings/task-types',
        resource: 'task-type',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.paymentMethods',
        url: '/app/settings/payment-methods',
        resource: 'payment-method',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.expenseCategories',
        url: '/app/settings/expense-categories',
        resource: 'expense-category',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.matterPracticeAreas',
        url: '/app/settings/matter-practice-areas',
        resource: 'matter-practice-area',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.contractPracticeAreas',
        url: '/app/settings/contract-practice-areas',
        resource: 'contract-practice-area',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.contractPeriods',
        url: '/app/settings/contract-periods',
        resource: 'contract-period',
        action: 'get',
      },
      {
        titleKey: 'common:navigation.qaCriteria',
        url: '/app/settings/qa-criteria',
        resource: 'qa-criteria',
        action: 'get',
      },
    ],
  },
];
